/**
 * 认证状态管理 Store
 *
 * 使用 zustand 管理认证状态，包括用户信息、加载状态等。
 * 使用 HttpOnly Cookie 存储 JWT Token，提供更安全的认证机制。
 * 同时保存 token 到内存，用于 WebSocket 连接。
 */

import { create } from 'zustand';
import { authApi, toUser } from '../services/authApi';
import type { User } from '../types/auth';

// ==================== 常量定义 ====================

const STORAGE_KEYS = {
  USER: 'user',
  TOKEN: 'access_token',  // 用于 WebSocket 连接
} as const;

// ==================== Store 状态接口 ====================

interface AuthState {
  // ===== 状态 =====
  user: User | null;
  token: string | null;  // JWT Token，用于 WebSocket 连接
  isAuthenticated: boolean;
  loading: boolean;
  initialized: boolean;  // 标记初始化是否完成
  error: string | null;

  // ===== 认证操作方法 =====
  logout: () => Promise<void>;

  // ===== 状态管理方法 =====
  initialize: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
}

// ==================== 存储辅助函数 ====================

/**
 * 保存用户信息到 localStorage
 */
const saveUserToStorage = (user: User): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  } catch (error) {
    console.error('[AuthStore] 保存用户信息到存储失败:', error);
  }
};

/**
 * 保存 token 到 sessionStorage（仅在会话期间有效）
 */
const saveTokenToStorage = (token: string): void => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
  } catch (error) {
    console.error('[AuthStore] 保存 token 到存储失败:', error);
  }
};

/**
 * 从存储中获取 token
 */
const getTokenFromStorage = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.TOKEN);
  } catch {
    return null;
  }
};

/**
 * 清除存储中的认证信息
 */
const clearAuthStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER);
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
  } catch (error) {
    console.error('[AuthStore] 清除存储失败:', error);
  }
};

// ==================== 创建 Auth Store ====================

export const useAuthStore = create<AuthState>((set, get) => ({
  // ===== 初始状态 =====
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  initialized: false,
  error: null,

  // ===== 认证操作 =====

  /**
   * 用户登出
   *
   * 调用 API 清除 Cookie，清除本地状态，跳转到登录页
   */
  logout: async () => {
    try {
      // 调用 API 清除 Cookie
      await authApi.logout();
    } catch (error) {
      console.error('[AuthStore] 登出 API 调用失败:', error);
      // 即使 API 调用失败，也继续清除本地状态
    }

    // 清除状态
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });

    clearAuthStorage();

    // 跳转到登录页
    window.location.href = '/login';
  },

  // ===== 状态管理方法 =====

  /**
   * 初始化认证状态
   *
   * 通过 API 验证 Cookie 中的 Token 是否有效。
   * 如果有效，恢复用户登录状态；否则清除本地状态。
   */
  initialize: async () => {
    const state = get();
    if (state.initialized) {
      console.log('[AuthStore] 初始化已完成，跳过');
      return;
    }

    // 如果已经有有效的认证状态（来自登录），跳过 API 调用
    if (state.isAuthenticated && state.token) {
      console.log('[AuthStore] 已有有效认证状态，跳过初始化');
      set({ initialized: true, loading: false });
      return;
    }

    set({ loading: true });

    try {
      // 尝试通过 API 获取当前用户信息（Cookie 自动携带）
      const response = await authApi.getCurrentUser();
      const user = toUser(response.user);

      // 优先使用 API 响应中的 token（用于 WebSocket 连接）
      // 如果 API 没有返回 token，则尝试从存储中恢复
      const token = response.access_token || getTokenFromStorage();

      // 恢复状态
      set({
        user,
        token,
        isAuthenticated: true,
        initialized: true,
        loading: false,
      });

      // 保存用户信息到本地存储
      saveUserToStorage(user);
      
      // 保存 token 到 sessionStorage（用于后续恢复）
      if (token) {
        saveTokenToStorage(token);
      }

      console.log('[AuthStore] 认证状态已恢复，用户:', user.email);
    } catch (error) {
      console.log('[AuthStore] 未登录或 Token 已过期');

      // 清除无效的认证数据
      clearAuthStorage();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        initialized: true,
        loading: false,
      });
    }
  },

  /**
   * 清除错误信息
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * 设置用户信息
   *
   * @param user 用户信息
   */
  setUser: (user: User | null) => {
    set({ user });
    if (user) {
      saveUserToStorage(user);
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  },

  /**
   * 设置 Token
   *
   * @param token JWT Token
   */
  setToken: (token: string | null) => {
    set({ token });
    if (token) {
      saveTokenToStorage(token);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    }
  },
}));

export default useAuthStore;